import React, { useMemo, useState } from "react";
import { searchOpenRouterModels } from "../openRouter.api";

const formatPrice = (price) => {
  if (price === null) return "—";
  if (price === 0) return "Free";
  if (price < 0.01) return `$${price.toFixed(4)}/M`;
  return `$${price.toFixed(2)}/M`;
};

const CanvasAiModelSelect = ({ isLoading, modelId, models, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(null);
  const selectedModel = models.find((model) => model.id === modelId);
  const inputValue = isOpen
    ? query ?? selectedModel?.name ?? ""
    : selectedModel?.name ?? "";
  const searchQuery = query ?? "";
  const visibleModels = useMemo(
    () => searchOpenRouterModels({ models, query: searchQuery }),
    [models, searchQuery]
  );

  const handleSelect = (nextModelId) => {
    onChange(nextModelId);
    setQuery(null);
    setIsOpen(false);
  };

  const handleBlur = () => {
    setQuery(null);
    setIsOpen(false);
  };

  const handleFocus = (event) => {
    setQuery(null);
    setIsOpen(true);
    event.currentTarget.select();
  };

  return (
    <div className="canvas-ai-model-select">
      <label className="canvas-ai-field-label" htmlFor="canvas-ai-model-search">
        Model
      </label>
      <input
        aria-autocomplete="list"
        aria-controls="canvas-ai-model-options"
        aria-expanded={isOpen}
        className="canvas-ai-input"
        disabled={isLoading}
        id="canvas-ai-model-search"
        onBlur={handleBlur}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={handleFocus}
        placeholder={isLoading ? "Loading models…" : "Search models…"}
        role="combobox"
        value={inputValue}
      />
      {selectedModel && !isOpen && (
        <p className="canvas-ai-model-meta">
          {selectedModel.provider} · {selectedModel.contextLength.toLocaleString()} ctx ·
          {` ${formatPrice(selectedModel.promptPrice)} in / ${formatPrice(
            selectedModel.completionPrice
          )} out`}
        </p>
      )}
      {isOpen && (
        <div
          aria-label="OpenRouter models"
          className="canvas-ai-model-options"
          id="canvas-ai-model-options"
          role="listbox"
        >
          {visibleModels.map((model) => (
            <button
              aria-selected={model.id === modelId}
              className="canvas-ai-model-option"
              key={model.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(model.id)}
              role="option"
              type="button"
            >
              <span className="canvas-ai-model-name">{model.name}</span>
              <span className="canvas-ai-model-meta">
                {model.id} · {formatPrice(model.promptPrice)} in · {formatPrice(model.completionPrice)} out
              </span>
            </button>
          ))}
          {!visibleModels.length && (
            <p className="canvas-ai-empty-state">No matching tool-capable models.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CanvasAiModelSelect;
